/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Injectable } from '@angular/core';
import { CacheFactoryService } from './factory/cache-factory.service';
import { ClinicalCacheFactory } from './factory/clinical-cache-factory';

@Injectable({
  providedIn: 'root'
})
export class PatientHistoryImagingService {
  private patientHistoryImagingCache: ClinicalCacheFactory;
  private comparisonsUpdateEvent = 'comparisonsUpdate';
  private idMap = {} as any;

  constructor(private cacheFactoryService: CacheFactoryService) {
    this.patientHistoryImagingCache = this.cacheFactoryService.createClinicalCacheFactory(
      'patientComparisonsCache',
      this.getEndpointFunction(),
      this.comparator
    );
  }

  getPatientComparisons(patientId, requestedProcedureId, refreshIfCached) {
    return this.patientHistoryImagingCache.get(patientId, refreshIfCached).then(data => {
      return this.cacheFactoryService.idMappingAddHandler(this.idMap, data, patientId, requestedProcedureId);
    });
  }

  removePatientComparisons(requestedProcedureId) {
    return this.patientHistoryImagingCache.remove(
      this.cacheFactoryService.idMappingRemoveHandler(this.idMap, requestedProcedureId)
    );
  }

  onComparisonsUpdate(callback, context) {
    return this.patientHistoryImagingCache.on(this.comparisonsUpdateEvent, callback, context);
  }

  getPatientHistoryImagingCache() {
    return this.patientHistoryImagingCache;
  }

  /************************** Private Functions **************************/

  private getEndpointFunction() {
    const endpointTemplate = 'patients/{id}/comparisons';
    const endpointNeedle = '{id}';
    return id => {
      return endpointTemplate.replace(endpointNeedle, id);
    };
  }

  private comparator = (patientId, cachedData, serverData) => {
    let needsUpdate = cachedData && cachedData.length !== serverData.length;

    if (!needsUpdate) {
      for (let j = 0; j < cachedData.length; j++) {
        needsUpdate = cachedData[j].requestedProcedureId !== serverData[j].requestedProcedureId;
        if (needsUpdate) {
          break;
        }
      }
    }

    if (needsUpdate) {
      this.patientHistoryImagingCache.fireEvent(this.comparisonsUpdateEvent, patientId, serverData, cachedData);
    }
    //Update cachedData with serverData
    this.patientHistoryImagingCache.updateCache(patientId, serverData);
  };
}
